import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import SectionHeading from "@/components/base/SectionHeading";
import { SITE } from "@/config/site";
import { getSiteStats } from "@/services/publicApi";
import { siteStats } from "@/mocks/siteContent";

type Stat = { id: string; value: string; label: string };

const pillars = [
  {
    id: "p-1",
    icon: "ri-search-eye-line",
    title: "One search, everything local",
    text: "Keyword, category and location work together, so customers land on the right business instead of scrolling endlessly.",
  },
  {
    id: "p-2",
    icon: "ri-shield-check-line",
    title: "Verified owners, real numbers",
    text: "Premium listings are checked against real addresses and owners, so contact details actually connect.",
  },
  {
    id: "p-3",
    icon: "ri-store-2-line",
    title: "Built for small businesses",
    text: "Free listings stay free. Owners upgrade only when they want verified badges and priority placement.",
  },
  {
    id: "p-4",
    icon: "ri-map-pin-2-line",
    title: "Genuinely hyperlocal",
    text: "Every listing is tied to a locality inside Mysore, Mandya, Chamarajanagar or Kodagu — not a generic pin.",
  },
];

const timeline = [
  { id: "t-1", year: "2023", title: "The idea", text: "We kept hearing the same complaint from friends in Mysore: finding a reliable local service online was pure guesswork." },
  { id: "t-2", year: "2024", title: "First listings", text: "We onboarded our first few hundred businesses across Mysore city and began mapping localities properly." },
  { id: "t-3", year: "2025", title: "Across the region", text: "Coverage expanded to Mandya, Chamarajanagar and Kodagu, alongside tourist spots and an owner portal." },
  { id: "t-4", year: "Today", title: "Still growing", text: "Thousands of listings, a new brand home and a team committed to keeping local discovery honest and simple." },
];

const aboutImage =
  "https://readdy.ai/api/search-image?query=Warm%20editorial%20photo%20of%20a%20lively%20local%20market%20street%20in%20Mysore%20India%20with%20colourful%20shops%20signboards%20and%20people%20walking%2C%20golden%20afternoon%20light%2C%20rich%20amber%20and%20olive%20tones%2C%20documentary%20photography%20style&width=1200&height=800&seq=myyp-about-01&orientation=landscape";

export default function About() {
  // Same source as the home hero: the public site-stats endpoint. Falls back to
  // the bundled defaults until it resolves, and keeps only the first four cards
  // so the About page always agrees with the hero.
  const [stats, setStats] = useState<Stat[]>(siteStats.slice(0, 4));

  useEffect(() => {
    let active = true;
    getSiteStats()
      .then((response) => {
        if (active) setStats(response.slice(0, 4));
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  return (
    <>
      <Seo
        title="About Us — Local Discovery Done Right"
        description="Mysore Yellow Pages is a hyperlocal business directory built to make finding trusted local businesses across Mysore, Mandya, Chamarajanagar and Kodagu simple and honest."
        keywords="about Mysore Yellow Pages, local business directory Karnataka, Mysore directory"
        path="/about"
      />

      <div className="border-b border-background-200 bg-background-100/70">
        <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-8 md:py-12">
          <Breadcrumbs items={[{ label: "About Us" }]} />
          <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary-800">
            <i className="ri-book-2-line"></i> About {SITE.name}
          </span>
          <h1 className="mt-4 max-w-3xl font-heading text-3xl md:text-5xl font-extrabold leading-tight tracking-tight text-foreground-950">
            Local discovery that actually works for Mysore
          </h1>
          <p className="mt-4 max-w-2xl text-sm md:text-lg text-foreground-600 leading-relaxed">
            We're a hyperlocal directory built by people who live here. Our goal is simple: help residents find
            reliable businesses nearby, and help those businesses get found by the customers who matter.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
          <div className="relative h-72 md:h-[420px] w-full overflow-hidden rounded-lg">
            <img
              src={aboutImage}
              alt="A busy local market street in Mysore"
              title="Mysore local market — Mysore Yellow Pages"
              className="h-full w-full object-cover object-top"
            />
          </div>
          <div>
            <SectionHeading
              eyebrow="Our mission"
              title="Make the neighbourhood findable"
              subtitle="For too long, discovering a good tailor, clinic, electrician or homestay nearby meant asking around. We're building the record of who's who across the region — accurate, searchable and free to browse."
            />
            <div className="mt-6 space-y-4">
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                Businesses get a fair, structured listing they control from an owner dashboard. Customers get
                real contact details, hours, reviews and locality-level results instead of a wall of ads.
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                We're independent and local. That means no pay-to-rank trickery — featured placement is clearly
                labelled, and every directory result is driven by relevance.
              </p>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/directory"
                className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-search-line"></i> Explore directory
              </Link>
              <Link
                to="/list-your-business"
                className="inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-800 hover:border-primary-400 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-add-circle-line"></i> List your business
              </Link>
            </div>
          </div>
        </div>
      </div>

      <section className="border-y border-background-200 bg-background-100/70">
        <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-12 md:py-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {stats.map((s) => (
              <div key={s.id} className="text-center lg:text-left">
                <div className="font-heading text-3xl md:text-4xl font-extrabold text-primary-700">{s.value}</div>
                <div className="mt-1.5 text-xs md:text-sm font-semibold text-foreground-600">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-12 md:py-16">
        <SectionHeading
          eyebrow="What we stand for"
          title="The principles behind every listing"
          subtitle="Four commitments that shape how the directory is built and run."
        />
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
          {pillars.map((p) => (
            <div key={p.id} className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                <i className={`${p.icon} text-xl`}></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{p.title}</h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </div>

      <section className="border-y border-background-200 bg-background-100/70">
        <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-12 md:py-16">
          <SectionHeading eyebrow="Our story" title="How we got here" subtitle="A short history of the directory." />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {timeline.map((t) => (
              <div key={t.id} className="relative rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="inline-flex items-center rounded-full bg-secondary-100 px-3 py-1 text-xs font-bold text-secondary-900">
                  {t.year}
                </span>
                <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{t.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{t.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-12 md:py-16">
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <h2 className="font-heading text-xl md:text-3xl font-extrabold text-foreground-950">
              Covering {SITE.regions.join(", ")}
            </h2>
            <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
              We're expanding locality by locality across the Mysore region. If your area isn't covered yet,
              tell us — and if you run a business there, get listed today.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/locations"
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-map-pin-2-line"></i> Explore locations
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-800 hover:border-primary-400 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-send-line"></i> Get in touch
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}